import { useEffect, useMemo, useState } from "react";
import { FiEdit, FiEye, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import LoaderComp from "../../../components/LoaderComp";
import PaginationComp from "../../../components/PaginationComp";
import RenderToast from "../../../components/RenderToast";
import useDebounce from "../../../hooks/useDebounce";
import DeleteBrandModal from "../../../Modal/DeleteBrandModal";
import EditBrandModal from "../../../Modal/EditBrandModal";
import ViewBrandModal from "../../../Modal/ViewBrandModal";
import API from "../../../utils/api";
import classes from "./ViewBrands.module.css";

const readUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function ViewBrands() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [editTarget, setEditTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [deletePayload, setDeletePayload] = useState({ open: false, ids: [] });
  const [deleting, setDeleting] = useState(false);
  const debouncedSearch = useDebounce(search, 400);
  const [user, setUser] = useState(readUser());
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const handleStorage = () => setUser(readUser());
    const handleUserUpdate = () => setUser(readUser());
    window.addEventListener("storage", handleStorage);
    window.addEventListener("userUpdated", handleUserUpdate);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("userUpdated", handleUserUpdate);
    };
  }, []);

  const can = (permission) => user && Number(user[permission]) === 1;
  const canViewBrand = can("brand_view");
  const canAddBrandButton = can("brand_view_add_button");
  const canDeleteSelectedButton = can("brand_view_delete_selected_button");
  const canEditBrand = can("brand_view_edit_action");
  const canViewBrandAction = can("brand_view_view_action");
  const canDeleteBrand = can("brand_view_delete_action");

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await API.post("/brands.php", { action: "fetch" });
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      setBrands(list);
    } catch {
      RenderToast({ type: "error", message: "Failed to load brands" });
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const filteredBrands = useMemo(() => {
    if (!debouncedSearch.trim()) return brands;
    const term = debouncedSearch.toLowerCase();
    return brands.filter(
      (b) =>
        b.brandName.toLowerCase().includes(term) ||
        (b.brand_slug || "").toLowerCase().includes(term)
    );
  }, [brands, debouncedSearch]);

  const paginatedBrands = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredBrands.slice(start, start + pageSize);
  }, [filteredBrands, currentPage]);

  const toggleSelect = (id) => {
    if (!canDeleteSelectedButton) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // const toggleSelectAll = () => {
  //   if (!canDeleteSelectedButton) return;
  //   selected.size === filteredBrands.length
  //     ? setSelected(new Set())
  //     : setSelected(new Set(filteredBrands.map((b) => b.id)));
  // };

  // change in this line

  const toggleSelectAll = () => {
    if (!canDeleteSelectedButton) return;
    selected.size === paginatedBrands.length
      ? setSelected(new Set())
      : setSelected(new Set(paginatedBrands.map((b) => b.id)));
  };

  const openDelete = (ids) => {
    if (!canDeleteSelectedButton && !canDeleteBrand) return;
    setDeletePayload({ open: true, ids });
  };
  const closeDelete = () => setDeletePayload({ open: false, ids: [] });

  const handleDelete = async () => {
    if (
      (!canDeleteSelectedButton && !canDeleteBrand) ||
      !deletePayload.ids.length
    )
      return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("authToken") || "";
      const params = new URLSearchParams();
      if (deletePayload.ids.length === 1) {
        params.append("action", "delete");
        params.append("id", deletePayload.ids[0]);
      } else {
        params.append("action", "bulk_delete");
        params.append("ids", JSON.stringify(deletePayload.ids));
      }
      const response = await fetch(`${API.base}/brands.php`, {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });
      const data = await response.json();
      if (!response.ok || !data.success)
        throw new Error(data.error || "Failed to delete");
      RenderToast({
        type: "success",
        message: "Brand(s) deleted successfully",
      });
      closeDelete();
      setSelected(new Set());
      fetchBrands();
    } catch (err) {
      RenderToast({ type: "error", message: err.message });
    } finally {
      setDeleting(false);
    }
  };

  if (!canViewBrand) {
    return (
      <DashboardLayout>
        <div className={classes.page}>
          <div className={classes.emptyState}>
            You do not have permission to view brands.
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className={classes.page}>
        {/* --- ROW 1: Top Bar --- */}
        <div className={classes.topBar}>
          <div className={classes.topLeft}>
            <p className={classes.kicker}>Catalog</p>
            <h2 className={classes.title}>Brands Library</h2>
            <p className={classes.subtitle}>
              Manage all brands used when creating products.
            </p>
          </div>
          <div className={classes.topRight}>
            <div className={classes.searchBlock}>
              <FiSearch className={classes.searchIcon} />
              <input
                type="search"
                placeholder="Search brands..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {canAddBrandButton && (
              <button
                className={classes.btnPrimary}
                onClick={() => navigate("/brand/add")}
              >
                <FiPlus size={20} /> Add Brand
              </button>
            )}
            {canDeleteSelectedButton && (
              <button
                className={classes.dangerBtn}
                disabled={selected.size === 0}
                onClick={() => openDelete(Array.from(selected))}
              >
                <FiTrash2 size={20} />
                Selected ({selected.size})
              </button>
            )}
          </div>
        </div>

        {/* --- ROW 2: Table --- */}
        <div className={classes.tableWrapper}>
          {loading ? (
            <LoaderComp />
          ) : filteredBrands.length === 0 ? (
            <div className={classes.emptyState}>No brands found.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  {canDeleteSelectedButton && (
                    <th className={classes.checkboxCell}>
                      {/* <input
                        type="checkbox"
                        onChange={toggleSelectAll}
                        checked={selected.size === filteredBrands.length}
                      /> */}

                      {/* change in this line  */}
                      <input
                        type="checkbox"
                        onChange={toggleSelectAll}
                        checked={
                          paginatedBrands.every((b) => selected.has(b.id)) &&
                          paginatedBrands.length > 0
                        }
                      />
                    </th>
                  )}
                  <th>S.No</th>
                  <th>Brand Name</th>
                  <th>Slug</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedBrands.map((brand, index) => (
                  <tr key={brand.id}>
                    {canDeleteSelectedButton && (
                      <td className={classes.checkboxCell}>
                        <input
                          type="checkbox"
                          checked={selected.has(brand.id)}
                          onChange={() => toggleSelect(brand.id)}
                        />
                      </td>
                    )}
                    <td>{(currentPage - 1) * pageSize + index + 1}</td>

                    <td>{brand.brandName}</td>
                    <td>
                      <span className={classes.brandSlug}>
                        {brand.brand_slug}
                      </span>
                    </td>
                    <td>
                      <div className={classes.tableActions}>
                        {canViewBrandAction && (
                          <button
                            className={classes.iconBtn}
                            title="View"
                            onClick={() => setViewTarget(brand)}
                          >
                            <FiEye />
                          </button>
                        )}
                        {canEditBrand && (
                          <button
                            className={classes.iconBtnGreen}
                            title="Edit"
                            onClick={() => setEditTarget(brand)}
                          >
                            <FiEdit />
                          </button>
                        )}
                        {canDeleteBrand && (
                          <button
                            className={classes.iconBtnRed}
                            title="Delete"
                            onClick={() => openDelete([brand.id])}
                          >
                            <FiTrash2 />
                          </button>
                        )}
                        {!canViewBrandAction &&
                          !canEditBrand &&
                          !canDeleteBrand && (
                            <span className={classes.noActions}>—</span>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* --- ROW 3: Footer (Total + Pagination) --- */}
        {filteredBrands.length > 0 && (
          <div className={classes.footerRow}>
            <div className={classes.totalLeft}>
              Total Brands: {filteredBrands.length}
            </div>
            <div className={classes.paginationRight}>
              {filteredBrands.length > pageSize && (
                <PaginationComp
                  total={filteredBrands.length}
                  perPage={pageSize}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <EditBrandModal
        brand={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={fetchBrands}
      />
      {viewTarget && canViewBrandAction && (
        <ViewBrandModal
          brand={viewTarget}
          onClose={() => setViewTarget(null)}
        />
      )}
      {deletePayload.open && (
        <DeleteBrandModal
          count={deletePayload.ids.length}
          loading={deleting}
          onCancel={closeDelete}
          onConfirm={handleDelete}
        />
      )}
    </DashboardLayout>
  );
}
